import { DatabaseZap, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import type { AccessProblem } from '~/store/taxonomy-studio.store';

/**
 * Full-page messages that replace the whole studio, instead of one toast per failed
 * call: no access (401 / NOT_PLATFORM_ADMIN / INSUFFICIENT_SCOPE), or a taxonomy that has
 * never been imported (NOT_INITIALIZED — nothing to show or edit yet).
 */
export function StudioBlocker({
  access,
  onRetry,
}: {
  /** Null means the blocker is "never imported". */
  access: AccessProblem | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation('taxonomy-studio');
  const sessionExpired =
    access?.code === 'INVALID_USER_TOKEN' ||
    access?.code === 'UNAUTHENTICATED' ||
    access?.status === 401;
  const Icon = access ? ShieldAlert : DatabaseZap;
  const title = access
    ? sessionExpired
      ? t('blocker.sessionTitle')
      : t('blocker.accessTitle')
    : t('blocker.notInitializedTitle');
  const body = access
    ? sessionExpired
      ? t('blocker.sessionBody')
      : t('blocker.accessBody')
    : t('blocker.notInitializedBody');

  return (
    <div className="flex h-full items-center justify-center p-8">
      <div role="alert" className="max-w-md space-y-3 text-center">
        <Icon className="text-muted-foreground mx-auto h-10 w-10" aria-hidden />
        <h1 className="text-foreground text-lg font-medium">{title}</h1>
        <p className="text-muted-foreground text-sm">{body}</p>
        {access?.detail && <p className="text-muted-foreground text-xs">{access.detail}</p>}
        <Button
          variant="outline"
          className="cursor-pointer"
          onClick={() => {
            if (sessionExpired) window.location.reload();
            else onRetry();
          }}
        >
          {sessionExpired ? t('blocker.signInAgain') : t('errors.action.retry')}
        </Button>
      </div>
    </div>
  );
}
